// Modal that shows a plain-text export of one or more releases/features and
// lets the user copy it to their clipboard. Consumers build the text
// (usually via lib/exportText.ts) and pass it in.
import { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/ui/dialog";
import { Button } from "@/ui/button";
import { useTrack } from "@/lib/track";

export function ExportTextModal({
  open,
  onOpenChange,
  text,
  title = "Export as text",
  description = "Copy the block below and paste into an LLM. Edit before copying if you need to.",
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  text: string;
  title?: string;
  description?: string;
}) {
  const [copied, setCopied] = useState(false);
  const [err, setErr] = useState<string>("");
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const track = useTrack();

  // Reset feedback when the modal opens fresh or content changes.
  useEffect(() => {
    if (open) {
      setCopied(false);
      setErr("");
    }
  }, [open, text]);

  const copy = async () => {
    setErr("");
    const el = textareaRef.current;
    const value = el?.value ?? text;
    try {
      // navigator.clipboard is https-only. Cloud Run gives us https; local
      // dev on http://localhost also gets it. document.execCommand is the
      // fallback for anything else.
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(value);
      } else if (el) {
        el.select();
        document.execCommand("copy");
      } else {
        throw new Error("clipboard unavailable");
      }
      setCopied(true);
      track({
        event: "export",
        meta: { bytes: new Blob([value]).size, lines: value.split("\n").length },
      });
      window.setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  };

  const byteCount = new Blob([text]).size;
  const lineCount = text ? text.split("\n").length : 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <textarea
          ref={textareaRef}
          defaultValue={text}
          rows={20}
          key={text}
          className="w-full rounded-md border border-border-hover bg-bg p-3 font-mono text-xs leading-snug focus:border-accent focus:outline-none"
        />

        <div className="flex items-center justify-between text-xs text-text-tertiary">
          <span>
            {lineCount.toLocaleString()} lines · {byteCount.toLocaleString()} bytes
          </span>
          {err && <span className="text-destructive">{err}</span>}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
          <Button onClick={copy}>{copied ? "Copied ✓" : "Copy to clipboard"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
